/**
 * Extractors for `getPlantEquipmentRelationships` — BOM truncation with
 * ancestor closure, title/symptom/cause → `name` fallbacks, and the
 * nested fault grouping the UI panels consume.
 */

import { extractNode, extractRawNode } from '../graph-row'
import { compareByNameThenId } from '../sort'
import type {
  CypherReadResult,
  PlantArea,
  PlantEquipment,
  PlantEquipmentRelationships,
  PlantExhibitsSymptomStatus,
  PlantFaultCauseRef,
  PlantFaultSymptomRef,
  PlantMesEntityRef,
  PlantPartRef,
  PlantProcedureRef
} from '../types'
import { extractPlantEdges } from './edges'

/** Max BOM parts returned after ancestor-preserving truncation. */
export const BOM_PART_DISPLAY_CAP = 500

/**
 * Prefer an explicit display field when the node has no `name`
 * (`Procedure.title`, `FaultSymptom.symptom`, `FaultCause.cause`).
 */
export function withDisplayName(value: unknown, fields: readonly string[]): unknown {
  const raw = extractRawNode(value)
  if (!raw) return null
  if (typeof raw['name'] === 'string' && raw['name'].length > 0) return raw
  for (const field of fields) {
    const candidate = raw[field]
    if (typeof candidate === 'string' && candidate.length > 0) {
      return { ...raw, name: candidate }
    }
  }
  return raw
}

function extractNamedList<T extends { id: string; _id: string; name: string }>(
  value: unknown,
  displayFields: readonly string[] = []
): T[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) =>
      extractNode<T>(displayFields.length ? withDisplayName(item, displayFields) : item)
    )
    .filter((node): node is T => node !== null)
    .sort(compareByNameThenId)
}

export function extractProcedureList(value: unknown): PlantProcedureRef[] {
  return extractNamedList<PlantProcedureRef>(value, ['title'])
}

const EXHIBITS_STATUSES: readonly PlantExhibitsSymptomStatus[] = [
  'documented',
  'monitored',
  'observed'
]

/**
 * Causes arrive as `{ cause, confirmations }` rows (derived through
 * Problems) but a bare cause node is still accepted so older fixtures and
 * hand-written rows keep working. Sorted by confirmation count, then name.
 */
function extractFaultCauseList(value: unknown): PlantFaultCauseRef[] {
  if (!Array.isArray(value)) return []
  const causes: PlantFaultCauseRef[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const wrapped = 'cause' in row && row['cause'] !== null && typeof row['cause'] === 'object'
    const node = extractNode<PlantFaultCauseRef>(
      withDisplayName(wrapped ? row['cause'] : row, ['cause'])
    )
    if (!node) continue
    const confirmations =
      wrapped && typeof row['confirmations'] === 'number' && row['confirmations'] >= 0
        ? row['confirmations']
        : undefined
    causes.push(confirmations === undefined ? node : { ...node, confirmations })
  }
  return causes.sort(
    (a, b) => (b.confirmations ?? 0) - (a.confirmations ?? 0) || compareByNameThenId(a, b)
  )
}

export function extractFaultList(value: unknown): PlantFaultSymptomRef[] {
  if (!Array.isArray(value)) return []
  const faults: PlantFaultSymptomRef[] = []
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const row = item as Record<string, unknown>
    const symptom = extractNode<PlantFaultSymptomRef>(withDisplayName(row['symptom'], ['symptom']))
    if (!symptom) continue
    const status = row['exhibits_status']
    const fault: PlantFaultSymptomRef = {
      ...symptom,
      causes: extractFaultCauseList(row['causes'])
    }
    if (typeof status === 'string' && (EXHIBITS_STATUSES as readonly string[]).includes(status)) {
      fault.exhibits_status = status as PlantExhibitsSymptomStatus
    }
    if (typeof row['exhibits_source'] === 'string' && row['exhibits_source'].trim()) {
      fault.exhibits_source = row['exhibits_source']
    }
    if (typeof row['first_observed_at'] === 'string') {
      fault.first_observed_at = row['first_observed_at']
    }
    if (typeof row['last_observed_at'] === 'string') {
      fault.last_observed_at = row['last_observed_at']
    }
    faults.push(fault)
  }
  return faults.sort(compareByNameThenId)
}

/**
 * Keep at most {@link BOM_PART_DISPLAY_CAP} parts in BFS order from the
 * equipment root, retaining every ancestor of every kept part (edges
 * whose endpoints are both kept, or whose source is the equipment).
 */
export function truncateBomWithAncestorClosure(
  equipmentId: string,
  parts: PlantPartRef[],
  edges: ReturnType<typeof extractPlantEdges>
): {
  parts: PlantPartRef[]
  edges: ReturnType<typeof extractPlantEdges>
  bomTruncated: boolean
} {
  if (parts.length <= BOM_PART_DISPLAY_CAP) {
    return { parts, edges, bomTruncated: false }
  }

  const partById = new Map(parts.map((part) => [part.id, part]))
  const childrenByParent = new Map<string, string[]>()
  for (const edge of edges) {
    if (edge.type !== 'HAS_PART') continue
    const list = childrenByParent.get(edge.source) ?? []
    list.push(edge.target)
    childrenByParent.set(edge.source, list)
  }

  const keptOrder: string[] = []
  const seen = new Set<string>()
  const queue = [...(childrenByParent.get(equipmentId) ?? [])]
  for (const id of queue) seen.add(id)

  while (queue.length > 0 && keptOrder.length < BOM_PART_DISPLAY_CAP) {
    const id = queue.shift()!
    if (!partById.has(id)) continue
    keptOrder.push(id)
    for (const child of childrenByParent.get(id) ?? []) {
      if (seen.has(child)) continue
      seen.add(child)
      queue.push(child)
    }
  }

  const keptIds = new Set(keptOrder)
  return {
    parts: keptOrder
      .map((id) => partById.get(id))
      .filter((part): part is PlantPartRef => part !== undefined),
    edges: edges.filter(
      (edge) =>
        edge.type === 'HAS_PART' &&
        (edge.source === equipmentId || keptIds.has(edge.source)) &&
        keptIds.has(edge.target)
    ),
    bomTruncated: true
  }
}

export function mapPlantEquipmentRelationships(
  raw: CypherReadResult,
  equipmentId: string
): PlantEquipmentRelationships {
  const row = raw.results[0] ?? {}
  const bomParts = extractNamedList<PlantPartRef>(row['bomParts'])
  const bomEdges = extractPlantEdges(row['bomEdges']).filter((edge) => edge.type === 'HAS_PART')
  const truncated = truncateBomWithAncestorClosure(equipmentId, bomParts, bomEdges)

  return {
    bomParts: truncated.parts,
    bomEdges: truncated.edges,
    bomTruncated: truncated.bomTruncated,
    subEquipment: extractNamedList<PlantEquipment>(row['subEquipment']),
    connections: extractNamedList<PlantEquipment>(row['connections']),
    procedures: extractProcedureList(row['procedures']),
    faults: extractFaultList(row['faults']),
    areas: extractNamedList<PlantArea>(row['areas']),
    mesUsage: {
      recipes: extractNamedList<PlantMesEntityRef>(row['mesRecipes']),
      phases: extractNamedList<PlantMesEntityRef>(row['mesPhases']),
      batches: extractNamedList<PlantMesEntityRef>(row['mesBatches']),
      operations: extractNamedList<PlantMesEntityRef>(row['mesOperations'])
    }
  }
}
